// Copyright (c) 2020 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import * as React from "react";
import { connect } from "react-redux";
import { Button } from "react-bootstrap";
import { FormattedMessage, defineMessages, WrappedComponentProps, injectIntl } from "react-intl";

import { IState } from "states/states";
import { IAction, ActionType, raiseAction, IActionAckAlarm } from "actions";
import { CloudSettings } from "dbtypes";
import { AlarmState, IAlarmEntry } from "comtypes";
import { IParameter } from "parameter";
import { isSet } from "utils";
import AlarmModel from "model/alarm";

import FormattedDate from "./FormattedDate";
import FormattedTime from "./FormattedTime";

interface IAlarmEntryExtProps {
  index: number;
  isEditable: boolean;
  filterText?: string;
}

type IAlarmEntryProps = IAlarmEntry & {
  parameter: IParameter;
  cloudSettings: CloudSettings | undefined;
};

const mapStateToProps = (state: IState, ownProps: IAlarmEntryExtProps) => {
  const site = state.site.siteList?.find((curSite) => curSite.id === state.cached.selectedSite);
  const props: IAlarmEntryProps = {
    ...state.data.alarms[ownProps.index],
    parameter: state.parameter!,
    cloudSettings: site?.cloudSettings,
  };
  return props;
};

interface IAlarmEntryDispatch {
  onAlarmAck: (xaid: string, alarmTime: number, source: string, value: number) => void;
}

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: IAlarmEntryDispatch = {
    onAlarmAck: (xaid: string, alarmTime: number, source: string, value: number) => {
      const payload: IActionAckAlarm = {
        xaid,
        alarmTime,
        source,
        value,
      };
      dispatch(raiseAction(ActionType.ACT_ACK_ALARM_REQ, payload));
    },
  };
  return dispatchProps;
};

const messages = defineMessages({
  ackTooltip: {
    id: "acknowledge this alarm and older alarms of same type",
  },
});

class AlarmEntryComponent extends React.Component<
  IAlarmEntryProps & IAlarmEntryExtProps & IAlarmEntryDispatch & WrappedComponentProps
> {
  public render() {
    const alarmModel = new AlarmModel(this.props, this.props.parameter, this.props.cloudSettings);

    if (
      !isSet(this.props.filterText) ||
      alarmModel
        .getText()
        .toLocaleLowerCase()
        .includes(this.props.filterText.toLocaleLowerCase()) ||
      this.props.intl
        .formatMessage({ id: `AlarmSource_${alarmModel.data.source}` })
        .toLocaleLowerCase()
        .includes(this.props.filterText.toLocaleLowerCase())
    ) {
      return (
        <tr className="alarmentryitem">
          <td className="center">
            <FormattedDate valueMs={this.props.alarmTime} />{" "}
            <FormattedTime valueMs={this.props.alarmTime} />
          </td>
          <td>
            <FormattedMessage id={`AlarmSource_${this.props.source}`} />
          </td>
          <td className="optional">{alarmModel.getText()}</td>
          {this.props.isEditable && (
            <td className="alarmAck">
              {![AlarmState.ACKD, AlarmState.ACKD_ACTIVE].includes(this.props.state) && (
                <Button
                  style={{ marginBottom: 2 }}
                  id="graphforward"
                  variant="secondary"
                  className="me-1"
                  title={this.props.intl.formatMessage(messages.ackTooltip)}
                  onClick={(event) =>
                    this.props.onAlarmAck(
                      this.props.xaid,
                      this.props.alarmTime,
                      this.props.source,
                      this.props.value,
                    )
                  }
                >
                  <FormattedMessage id="Acknowledge" />
                </Button>
              )}
            </td>
          )}
        </tr>
      );
    }
  }

  private getStateText() {
    if (this.props.state === AlarmState.ACTIVE) return <FormattedMessage id="active" />;
    if (this.props.state === AlarmState.ACK_PENDING) return <FormattedMessage id="pending" />;
    if (this.props.state === AlarmState.ACKD_ACTIVE)
      return <FormattedMessage id="active, acknowledged" />;
    // (this.props.state === AlarmState.ACK)
    return <FormattedMessage id="acknowledged" />;
  }
}

const AlarmEntry = injectIntl(connect(mapStateToProps, mapDispatchToProps)(AlarmEntryComponent));

export default AlarmEntry;
